import { DISCIPLINE_LABEL, TIMER_STATE_LABEL, formatMs, penaltyCodeLabel } from '@flm9/i18n/labels';
import { liveQuery } from '@flm9/sync';
import {
  AUTH_PAUSE_ERROR,
  disqualifyRoundOrQueue,
  queueRoundOp,
  refereeDrainer,
  resumeAfterSignIn,
  saveRoundTimingInOrder,
  wakeRefereeSync,
  type TimingPatch,
} from '../lib/sync';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getLocalDb,
  assignmentOf,
  assignmentOutboxView,
  retryAssignment,
  reconcileAssignment,
  type CachedShot,
  type OutboxEntry,
} from '@flm9/sync';
import {
  CameraPanel,
  HitIndicator,
  useStreamMeta,
  type TargetZone,
  type VisionDetection,
} from '@flm9/stream-client';
import { ipscPoints, roundPoints } from '@flm9/scoring';
import { IpscKeypad } from './IpscKeypad';
import { NumericKeypad } from './NumericKeypad';
import { TimingPad, type TimingPadHandle } from './TimingPad';
import { SignInScreen } from './SignInScreen';
import { DqReasonSheet } from './DqReasonSheet';
import { PenaltySection } from './PenaltySection';
import { formatPenaltyPoints, usePenalties, type PenaltyValues } from '../lib/penalties';
import { api, type ShotPositionRow, errorCode, isOffline, UnauthenticatedError } from '../lib/api';
import { errorText } from '../lib/errorText';
import { useTerm } from '../lib/wording';
import { userErrorText } from '../lib/userErrorText';
import { getLastRosterCall, setLastRosterCall } from '../lib/rosterCall';
import { SnapshotComparison } from '../components/SnapshotComparison';
import { useClaimArmedRun, useTimer, useTimerRun } from '../lib/timer/provider';
import { RANDOM_START_DELAY_DS } from '../lib/timer/sg';
import { formatCountUp, useCountUpTimer } from '../lib/timer/countUp';
import { useRoundNeedsCalibration } from '../lib/calibration';

/** Bottom bar that stays on screen while the page scrolls (UX review REF-12). */
const STICKY_BAR =
  'sticky bottom-0 z-30 -mx-4 border-t border-zinc-800 bg-zinc-950/95 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur';

/**
 * "What · why" for a change the server rejected. `lastError` is the server's
 * German reason (lib/sync.ts); entries rejected before that carry `http_4xx`.
 */
function failedReason(entry: OutboxEntry, shots: readonly CachedShot[]): string {
  const payload = entry.payload as { ord?: number; clientIdempotencyKey?: string };
  const ord =
    payload.ord ?? shots.find((s) => s.clientIdempotencyKey === payload.clientIdempotencyKey)?.ord;
  const shot = ord != null ? `Schuss #${ord}` : 'Schuss';
  const penaltyCode = (entry.payload as { code?: string }).code;
  const penalty = penaltyCode ? `Strafe (${penaltyCodeLabel(penaltyCode)})` : 'Strafe';
  const labels: Partial<Record<OutboxEntry['op'], string>> = {
    'penalty.add': penalty,
    'penalty.remove': `${penalty} (Entfernen)`,
    'shot.add': shot,
    'shot.update': `${shot} (Korrektur)`,
    'shot.remove': `${shot} (Löschen)`,
    'round.timing': 'Zeit',
    'round.complete': 'Bestätigung',
    'round.call': 'Aufruf',
    'round.dq': 'Disqualifikation',
  };
  const what = labels[entry.op] ?? entry.op;
  const why =
    !entry.lastError || /^http_\d+$/.test(entry.lastError)
      ? `vom Server abgelehnt${entry.lastError ? ` (${entry.lastError.replace('http_', 'HTTP ')})` : ''}`
      : entry.lastError;
  return `${what}: ${why}`;
}

function FailedReasons({ reasons }: { reasons: readonly string[] }) {
  if (reasons.length === 0) return null;
  return (
    <ul role="alert" className="mt-2 space-y-1 text-left text-xs text-amber-200">
      {reasons.map((r, i) => (
        <li key={i}>⚠️ {r}</li>
      ))}
    </ul>
  );
}

/** How long "{Name} aufgerufen – Rückgängig" stays after a roster call (REF-18). */
const ROSTER_UNDO_MS = 10_000;

const HIT_PIP_LS_KEY = 'flm9.referee.hitPip';
const STREAM_PIP_LS_KEY = 'flm9.referee.streamPip';

interface VisionDetectionRow {
  id: string;
  xNorm: number;
  yNorm: number;
  /** Template-local coords (post-homography). Used by the hit indicator. */
  targetXNorm: number;
  targetYNorm: number;
  valueNumeric: number | null;
  zoneCode: string | null;
  detectedAt: string;
  roundAssignmentId: string | null;
  /** Set when the referee has accepted this detection as a shot. */
  consumedShotId: string | null;
  /** Set when the referee has actively rejected this detection. */
  dismissedAt: string | null;
}

interface ScoreScreenProps {
  /** Parent session id — needed for stream metadata, vision detections,
   *  and the SSE presence channel that wakes the vision worker. */
  sessionId: string;
  assignmentId: string;
  /** The round this assignment belongs to — used to arm the round
   *  (vision spin-up). */
  roundId: string;
  /** ISO timestamp when the round was armed; null until the referee
   *  taps "Runde scharfschalten" or starts the SG-Timer. Drives the
   *  Arm button's visibility. */
  roundArmedAt: string | null;
  shotCount: number;
  /**
   * Optional magazine breakdown (ordered per-mag shot counts summing to
   * shotCount). NULL = single magazine. Drives the "Nachladen" cue shown
   * when the recorded shot count reaches a magazine boundary.
   */
  magazines: Array<{ shots: number }> | null;
  shooterLabel: string;
  scoringSchemaCode: string;
  scoringSchemaKind: 'ring_value' | 'hit_factor';
  /** Schema config (IPSC zone points / power factor) for the confirmed-round total. */
  scoringSchemaConfig?: unknown;
  scoringSchemaZones: Array<{
    zoneCode: string;
    valueNumeric: number;
    ordering: number;
    displayColor: string | null;
  }>;
  /** Geometric zone definitions for the stage's target — drives the hit
   *  indicator's shape-faithful rendering. */
  targetZones: Array<{
    zoneCode: string;
    ordering: number;
    shape: 'circle' | 'rect' | 'polygon';
    geometry: unknown;
    displayColor: string | null;
  }>;
  /** Round-level penalties accrued for this assignment. */
  penalties: Array<{
    id: string;
    code: 'procedural' | 'miss' | 'no_shoot' | 'extra_shot';
    points: number;
    note?: string | null;
    clientIdempotencyKey?: string | null;
  }>;
  /**
   * Points per penalty type from the stage's scoring schema (liveAssignments
   * `penaltyValues`). Preview only — the server derives the stored points.
   */
  penaltyValues?: PenaltyValues | undefined;
  /** Output mode picked at drill instantiation. Drives whether
   *  timing is required and which leaderboard ranking applies. */
  scoringMode: 'precision' | 'speed';
  /** Distance to target in centimetres — display-only in the header. */
  distanceCm: number;
  /** Per-stage discipline code (pistol / pistol_carbine / rifle) or null —
   *  display-only chip in the header. */
  disciplineCode: string | null;
  /** Timer safety auto-STOP in seconds (resolved server-side: 2×PAR when a
   *  PAR is set, else the per-session override / club default). Bounds
   *  recording so a run never records forever; STOPS the run, does NOT
   *  disqualify. NULL disables the auto-stop. */
  maxRecordingSeconds: number | null;
  /**
   * Set when the stage was created from a scenario slot — drives the
   * scenario-mode header chip and the count-up display. NULL on plain
   * drill stages.
   */
  scenarioTitle: string | null;
  /**
   * Effective PAR (referee override → drill), already resolved by the
   * API. Drives the timer push in hard mode and the scoreboard tint in
   * soft mode.
   */
  effectiveParSeconds: number | null;
  /**
   * Effective PAR enforcement mode.
   *  - `soft`: informational only (amber cue), never DQ.
   *  - `hard` ("hart-offen"): auto-DQs on exceed but keeps recording shot
   *    timings until the shot count / 2×PAR; the DQ is liftable.
   *  - `hard_stop` ("hart-stopp"): stops the timer instantly on exceed and
   *    the DQ is terminal.
   */
  effectiveParMode: 'soft' | 'hard' | 'hard_stop';
  /** ISO timestamp when the round was confirmed; renders read-only when set. */
  completedAt: string | null;
  /** Persisted timing values on the assignment (for hydrating TimingPad). */
  initialFirstShotMs: number | null;
  initialElapsedMs: number | null;
  /** When set, the round is disqualified; reason is shown in the banner. */
  disqualifiedAt: string | null;
  dqReason: string | null;
  /**
   * Label of the next pending shooter on the same stage+round, or null
   * when this is the last one. Drives "Bestätigen" (stay here) vs.
   * "Runde bestätigen — Abschluss" and the "📣 Aufrufen" button.
   */
  nextShooterLabel: string | null;
  /**
   * Set when "Aufrufen" found someone else up next than the button showed
   * (REF-19): nobody was called, the button now names that shooter.
   */
  nextShooterNotice?: string | null;
  /** "Aufrufen" — calls (= claims) the next pending shooter and opens them. */
  onCallNext: () => Promise<void>;
  /** When this shooter was called ("Aufgerufen"); null on legacy/unknown. */
  calledAt?: string | null;
  /** When standby (timer start announcement) was set; null when not. */
  standbyAt?: string | null;
  /** Returns to the roster view. */
  onBack: () => void;
  /** After the last shooter's Bestätigen: the round-complete screen (REF-16). */
  onRoundComplete: () => void;
  /** Refresh the parent's roster after a DQ so the lock banner reflects state. */
  /** Resolves `true` once the roster was refreshed (false/void: not refreshed). */
  onRefetchRoster?: (() => void | boolean | Promise<void | boolean>) | undefined;
  /** Opens the timer debug / pairing screen from the SG-Timer tab. */
  onOpenTimerDebug?: (() => void) | undefined;
  /**
   * When true, TimingPad surfaces the phone-mic timer tab (beta).
   * Wired to `session.signOffState === 'self_signed'` upstream.
   */
  allowPhoneTimer?: boolean;
}

/**
 * DQ reason the hard-PAR auto-DQ writes (ScoreTimerBar). The server stores
 * it as `dq_reason`, so after a refetch the referee can still tell a PAR
 * auto-DQ (hart-offen keeps recording) from a manual DQ (UI collapses).
 */
const PAR_AUTO_DQ_REASON = 'PAR überschritten (Auto-DQ, harter PAR)';

export function ScoreScreen({
  sessionId,
  assignmentId,
  roundId,
  roundArmedAt,
  shotCount,
  magazines,
  shooterLabel,
  scoringSchemaCode,
  scoringSchemaKind,
  scoringSchemaConfig,
  scoringSchemaZones,
  targetZones,
  penalties,
  penaltyValues,
  scoringMode,
  distanceCm,
  disciplineCode,
  maxRecordingSeconds,
  scenarioTitle,
  effectiveParSeconds,
  effectiveParMode,
  completedAt,
  initialFirstShotMs,
  initialElapsedMs,
  disqualifiedAt,
  dqReason,
  nextShooterLabel,
  nextShooterNotice = null,
  onCallNext,
  calledAt = null,
  standbyAt = null,
  onBack,
  onRoundComplete,
  onRefetchRoster,
  onOpenTimerDebug,
  allowPhoneTimer = false,
}: ScoreScreenProps) {
  const t = useTerm();
  const [shots, setShots] = useState<CachedShot[]>([]);
  // Penalties = server rows + queued outbox entries; reconciled on open.
  const penaltyView = usePenalties({
    assignmentId,
    initial: penalties,
    values: penaltyValues,
  });
  const penaltyEntries = useMemo(
    () => penaltyView.rows.map((r) => ({ id: r.key, code: r.code, points: r.points ?? 0 })),
    [penaltyView.rows],
  );
  const [pending, setPending] = useState(0);
  const [failed, setFailed] = useState(0);
  // One line per change the server rejected: what and why (e.g. a value the
  // stage's scoring schema doesn't define).
  const [failedReasons, setFailedReasons] = useState<string[]>([]);
  // Shots of this round still in the outbox — each cell gets a dot (REF-9).
  const [unsentKeys, setUnsentKeys] = useState<ReadonlySet<string>>(new Set());
  // A DQ of this round queued offline (`round.dq`) and not on the server
  // yet: the screen already treats the round as DQ'd (survives a remount).
  const [queuedDq, setQueuedDq] = useState<{ reason: string; terminal: boolean } | null>(null);
  // A queued DQ that reached the server: kept until the roster shows it (the
  // outbox entry is gone once acked), else the screen would drop back to
  // normal scoring until some later refetch.
  const [landedDq, setLandedDq] = useState<string | null>(null);
  const queuedDqRef = useRef<{ reason: string; terminal: boolean } | null>(null);
  const onRefetchRosterRef = useRef(onRefetchRoster);
  onRefetchRosterRef.current = onRefetchRoster;
  // Once the roster has the DQ the server row decides (a DQ lifted later on
  // the web brings the keypad back).
  useEffect(() => {
    if (disqualifiedAt != null) setLandedDq(null);
  }, [disqualifiedAt]);
  const confirmingRef = useRef(false);
  const [confirming, setConfirming] = useState(false);
  // Scenario mode count-up — independent of the BLE par timer (which
  // counts DOWN). When scenarioTitle is set, the chip auto-starts on
  // the first shot landing and auto-stops when the round is
  // confirmed. Read-only display; scenario timing for scoring still
  // comes from the canonical TimingPad / BLE timer paths.
  const scenarioTimer = useCountUpTimer();
  const scenarioTimerRef = useRef(scenarioTimer);
  scenarioTimerRef.current = scenarioTimer;
  // Optional picture-in-picture mode for the hit indicator. When on,
  // the indicator floats bottom-right and the inline copy inside the
  // keypad / locked view is suppressed. Persisted so a referee who
  // pops it out keeps that preference between rounds.
  const [hitPip, setHitPipState] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(HIT_PIP_LS_KEY) === '1';
    } catch {
      return false;
    }
  });
  // Optional camera PiP — live go2rtc tile with vision-detected hit
  // markers, mirroring the public scoreboard. Mutually exclusive with
  // `hitPip` because both float in the same bottom-right slot. Opening
  // it also subscribes to the board SSE channel for this session,
  // which is what the vision service uses as a presence gate to leave
  // standby (apps/realtime increments a counter on connect, the
  // vision supervisor polls the realtime container, then spawns the
  // detector for that location).
  const [streamPip, setStreamPipState] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(STREAM_PIP_LS_KEY) === '1';
    } catch {
      return false;
    }
  });
  // The two PiPs share the same anchor; flipping one on flips the other
  // off so we never stack two floating tiles.
  const setHitPip: typeof setHitPipState = (v) => {
    setHitPipState((prev) => {
      const next = typeof v === 'function' ? (v as (p: boolean) => boolean)(prev) : v;
      if (next) setStreamPipState(false);
      return next;
    });
  };
  const setStreamPip: typeof setStreamPipState = (v) => {
    setStreamPipState((prev) => {
      const next = typeof v === 'function' ? (v as (p: boolean) => boolean)(prev) : v;
      if (next) setHitPipState(false);
      return next;
    });
  };
  const timingRef = useRef<TimingPadHandle | null>(null);

  // Local mirror of `roundArmedAt` so the Arm button hides immediately
  // after a successful arm without waiting for the parent's roster
  // refetch. Re-syncs to the prop on assignment change.
  const [armedAt, setArmedAt] = useState<string | null>(roundArmedAt);
  useEffect(() => {
    setArmedAt(roundArmedAt);
  }, [roundArmedAt, assignmentId]);

  // Scenario timer wiring. Reset on assignment change (next shooter on
  // the same scenario stage starts fresh); auto-start on the first shot
  // landing; auto-stop on confirm.
  useEffect(() => {
    scenarioTimerRef.current.reset();
    // Effect runs once per assignmentId change; we read through the
    // ref to avoid retriggering on every scenarioTimer state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId]);
  useEffect(() => {
    if (!scenarioTitle) return;
    const t = scenarioTimerRef.current;
    if (shots.length > 0 && !t.running && t.startedAt == null) {
      t.start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shots.length, scenarioTitle]);
  useEffect(() => {
    if (!scenarioTitle) return;
    if (completedAt && scenarioTimerRef.current.running) {
      scenarioTimerRef.current.stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedAt, scenarioTitle]);

  const armRound = async (silent: boolean) => {
    try {
      const res = await api.armRound(roundId);
      setArmedAt(res.armedAt ?? new Date().toISOString());
    } catch (err) {
      console.error('armRound failed', err);
      // A silent auto-arm (Standby / Timer Start) must not interrupt the run
      // start; the manual "Runde scharfschalten" tap reports the failure.
      if (!silent) alert(`Kamera-Erkennung konnte nicht gestartet werden: ${errorText(err)}`);
    }
  };

  useEffect(() => {
    try {
      window.localStorage.setItem(HIT_PIP_LS_KEY, hitPip ? '1' : '0');
    } catch {
      // ignore — storage may be unavailable in private mode.
    }
  }, [hitPip]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STREAM_PIP_LS_KEY, streamPip ? '1' : '0');
    } catch {
      // ignore
    }
  }, [streamPip]);

  const {
    isConnected: timerConnected,
    status: timerStatus,
    source: timerSource,
    claimRun,
  } = useTimer();

  // Stream metadata — only fetched when the PiP is on; passing null
  // disables the 30 s poll inside the hook so the off-state is free.
  const streamMeta = useStreamMeta(streamPip ? sessionId : null);

  // Vision detections, scoped to the active assignment. Refreshed via
  // a 30 s poll plus live SSE pushes (see effect below). Runs while
  // either PiP is open — the camera tile shows them as overlay markers
  // and the hit indicator shows them as confirmable ghosts.
  const [detections, setDetections] = useState<VisionDetectionRow[]>([]);
  const detectionsActive = streamPip || hitPip;
  useEffect(() => {
    if (!detectionsActive || !sessionId) {
      setDetections([]);
      return;
    }
    let aborted = false;
    const refetch = async () => {
      try {
        const params = new URLSearchParams({
          input: JSON.stringify({ sessionId }),
          batch: '0',
        });
        const res = await fetch(`/api/trpc/public.visionDetections?${params}`, {
          cache: 'no-store',
        });
        if (!res.ok) return;
        const body = (await res.json()) as {
          result?: { data?: VisionDetectionRow[] };
        };
        if (aborted) return;
        setDetections(body.result?.data ?? []);
      } catch {
        // best-effort; SSE will retry on the next push.
      }
    };
    void refetch();

    // Open SSE — also flips the realtime presence counter for this
    // session, which is the standby trigger for the vision service.
    let es: EventSource | null = null;
    let reconnect: number | null = null;
    const connect = () => {
      es = new EventSource(`/sse/board/${sessionId}`);
      es.addEventListener('vision.detection', () => void refetch());
      es.addEventListener('round.update', () => void refetch());
      es.addEventListener('error', () => {
        es?.close();
        es = null;
        reconnect = window.setTimeout(connect, 5000);
      });
    };
    connect();
    // Polling fallback in case SSE pushes are dropped.
    const poll = window.setInterval(() => void refetch(), 30_000);

    return () => {
      aborted = true;
      if (reconnect != null) window.clearTimeout(reconnect);
      window.clearInterval(poll);
      es?.close();
    };
  }, [detectionsActive, sessionId]);

  // Per-shot positions + before/after snapshot IDs for the active
  // assignment. Powers the new "📸 Schussbild ansehen" modal. Lighter
  // than the pending-detections fetch (one row per shot, no overlay
  // polling) so it can run regardless of PiP state — the modal is
  // available the moment the round is done.
  const [shotPositionRows, setShotPositionRows] = useState<ShotPositionRow[]>([]);
  const [snapshotPair, setSnapshotPair] = useState<{
    before: { id: string } | null;
    after: { id: string } | null;
  }>({ before: null, after: null });
  useEffect(() => {
    if (!assignmentId || !sessionId) {
      setShotPositionRows([]);
      setSnapshotPair({ before: null, after: null });
      return;
    }
    let aborted = false;
    const refetch = async () => {
      try {
        const [posRes, pairRes] = await Promise.allSettled([
          api.shotPositionsForAssignment(assignmentId),
          api.snapshotPairForAssignment(assignmentId),
        ]);
        if (aborted) return;
        if (posRes.status === 'fulfilled' && posRes.value) {
          setShotPositionRows(posRes.value);
        }
        if (pairRes.status === 'fulfilled' && pairRes.value) {
          setSnapshotPair({
            before: pairRes.value.before ? { id: pairRes.value.before.id } : null,
            after: pairRes.value.after ? { id: pairRes.value.after.id } : null,
          });
        }
      } catch {
        // best-effort — modal degrades to "nicht verfügbar"
      }
    };
    void refetch();
    let es: EventSource | null = null;
    let reconnect: number | null = null;
    const connect = () => {
      es = new EventSource(`/sse/board/${sessionId}`);
      es.addEventListener('shot.position', () => void refetch());
      es.addEventListener('snapshot.captured', () => void refetch());
      es.addEventListener('error', () => {
        es?.close();
        es = null;
        reconnect = window.setTimeout(connect, 5000);
      });
    };
    connect();
    return () => {
      aborted = true;
      if (reconnect != null) window.clearTimeout(reconnect);
      es?.close();
    };
  }, [assignmentId, sessionId]);
  const driftFlagged = useMemo(() => shotPositionRows.some((p) => p.drift), [shotPositionRows]);
  const calibrationGap = useRoundNeedsCalibration(sessionId, assignmentId);
  const [showComparison, setShowComparison] = useState(false);

  // Filter detections to the active assignment, sort, cap at 30, and
  // assign a 1-based ord. Mirrors the board's numberedDetections but
  // keyed on assignmentId since the referee knows exactly which round
  // is active — no need to guess "the latest one we've seen".
  const numberedDetections: VisionDetection[] = useMemo(() => {
    if (detections.length === 0) return [];
    const filtered = detections.filter((d) => d.roundAssignmentId === assignmentId);
    const sorted = [...filtered].sort(
      (a, b) => new Date(a.detectedAt).getTime() - new Date(b.detectedAt).getTime(),
    );
    return sorted.slice(-30).map((d, i) => ({
      id: d.id,
      xNorm: d.xNorm,
      yNorm: d.yNorm,
      ord: i + 1,
      valueNumeric: d.valueNumeric,
      zoneCode: d.zoneCode,
    }));
  }, [detections, assignmentId]);

  // Detections that haven't yet been turned into a shot — rendered
  // as dashed translucent ghosts on the hit indicator and surfaced
  // in the inline DetectionRecommendation banner.
  const pendingDetections = useMemo(
    () =>
      detections
        .filter(
          (d) =>
            d.roundAssignmentId === assignmentId &&
            d.consumedShotId == null &&
            d.dismissedAt == null,
        )
        .sort((a, b) => new Date(a.detectedAt).getTime() - new Date(b.detectedAt).getTime())
        .map((d) => ({
          id: d.id,
          x: d.targetXNorm,
          y: d.targetYNorm,
          valueNumeric: d.valueNumeric,
          zoneCode: d.zoneCode,
          confidence: 1,
        })),
    [detections, assignmentId],
  );

  // Real coordinates for shots that came from a confirmed detection
  // — passed to the hit indicator so it plots them where the bullet
  // actually hit rather than at the synthesised zone centroid.
  const shotPositions = useMemo(
    () =>
      detections
        .filter((d) => d.roundAssignmentId === assignmentId && d.consumedShotId != null)
        .map((d) => ({
          shotId: d.consumedShotId as string,
          x: d.targetXNorm,
          y: d.targetYNorm,
        })),
    [detections, assignmentId],
  );

  const handleConfirmDetection = async (detectionId: string) => {
    const det = detections.find((d) => d.id === detectionId);
    if (!det) return;
    // Pick the next free ord — local shots are the source of truth
    // here, the optimistic local insert below also bumps it. If the
    // server already has higher-ord shots they'll dominate after
    // the next reconciliation.
    const usedOrds = new Set(shots.map((s) => s.ord));
    let ord = 1;
    while (usedOrds.has(ord) && ord <= shotCount + 5) ord += 1;
    const idemKey = `vis:${detectionId}`;
    const ipscZones = ['A', 'C', 'D', 'M', 'NS'] as const;
    const zoneCode =
      det.zoneCode && (ipscZones as readonly string[]).includes(det.zoneCode)
        ? (det.zoneCode as 'A' | 'C' | 'D' | 'M' | 'NS')
        : null;
    try {
      const created = (await api.confirmDetection({
        detectionId,
        ord,
        valueNumeric: det.valueNumeric,
        zoneCode,
        clientIdempotencyKey: idemKey,
      })) as Partial<{
        ord: number;
        valueNumeric: number | string | null;
        zoneCode: string | null;
        clientIdempotencyKey: string;
      }> | null;
      // Put the new shot into the local shot cache right away (item 19):
      // the keypad picks its next ord from that cache, so without it the
      // next keypad shot would take this ord again (shot_ordinal_taken).
      // The next reconcile replaces it with the server row (same key).
      const key = created?.clientIdempotencyKey ?? idemKey;
      const rawValue =
        created?.valueNumeric !== undefined ? created.valueNumeric : det.valueNumeric;
      await getLocalDb().shots.put({
        id: key,
        roundAssignmentId: assignmentId,
        ord: created?.ord ?? ord,
        valueNumeric: rawValue == null ? null : Number(rawValue),
        zoneCode: created?.zoneCode !== undefined ? created.zoneCode : zoneCode,
        firstShotTimeMs: null,
        elapsedTimeMs: null,
        clientIdempotencyKey: key,
        recordedAt: Date.now(),
      });
      // Mark the local cache so the ghost disappears immediately;
      // the next poll will refresh from the server.
      setDetections((prev) =>
        prev.map((d) => (d.id === detectionId ? { ...d, consumedShotId: idemKey } : d)),
      );
      // Ask the parent to refetch the roster so the new shot shows
      // up in the UI on the next render cycle.
      onRefetchRoster?.();
    } catch (err) {
      console.error('confirmDetection failed', err);
      alert('Übernahme fehlgeschlagen — bitte erneut versuchen.');
    }
  };

  const handleDismissDetection = async (detectionId: string) => {
    try {
      await api.dismissDetection(detectionId);
      setDetections((prev) =>
        prev.map((d) =>
          d.id === detectionId ? { ...d, dismissedAt: new Date().toISOString() } : d,
        ),
      );
    } catch (err) {
      console.error('dismissDetection failed', err);
      alert(`Verwerfen fehlgeschlagen: ${errorText(err)}`);
    }
  };

  useEffect(() => {
    const db = getLocalDb();
    setQueuedDq(null);
    setLandedDq(null);
    queuedDqRef.current = null;
    const sub = liveQuery(async () => ({
      local: await db.shots.where('roundAssignmentId').equals(assignmentId).sortBy('ord'),
      queued: await db.outbox
        .where('status')
        .anyOf('pending', 'inflight', 'failed_terminal')
        .filter((e) => assignmentOf(e) === assignmentId)
        .toArray(),
    })).subscribe(({ local, queued }) => {
      setShots(local);
      setPending(queued.filter((e) => e.status !== 'failed_terminal').length);
      const rejected = queued.filter((e) => e.status === 'failed_terminal');
      setFailed(rejected.length);
      setFailedReasons(rejected.map((e) => failedReason(e, local)));
      const nextDq = assignmentOutboxView(
        assignmentId,
        queued.filter((e) => e.status !== 'failed_terminal'),
        rejected,
        local,
        AUTH_PAUSE_ERROR,
      ).queuedDq;
      const prevDq = queuedDqRef.current;
      queuedDqRef.current = nextDq;
      setQueuedDq(nextDq);
      if (prevDq && !nextDq) {
        // Left the queue: acked, discarded or rejected (failed dot). Kept
        // shown until a roster refresh says what the server has (a
        // discarded DQ never reached it; offline, an acked one stays shown).
        if (!rejected.some((e) => e.op === 'round.dq')) setLandedDq(prevDq.reason);
        void Promise.resolve(onRefetchRosterRef.current?.()).then((ok) => {
          if (ok === true) setLandedDq(null);
        });
      }
      setUnsentKeys(
        new Set(
          queued
            .filter((e) => e.op.startsWith('shot.'))
            .map((e) => (e.payload as { clientIdempotencyKey?: string }).clientIdempotencyKey)
            .filter((k): k is string => k != null),
        ),
      );
    });
    let active = true;
    const reconcile = async () => {
      try {
        await reconcileAssignment(assignmentId, async (id) => {
          const params = new URLSearchParams({
            input: JSON.stringify({ assignmentId: id }),
            batch: '0',
          });
          const res = await fetch(`/api/trpc/shots.listByAssignment?${params}`, {
            credentials: 'include',
            signal: AbortSignal.timeout(15_000),
          });
          if (!res.ok) throw new Error(`shots.list: HTTP ${res.status}`);
          const body = await res.json();
          if (!Array.isArray(body.result?.data)) throw new Error('invalid shot list');
          return body.result.data;
        });
        if (active) wakeRefereeSync();
      } catch (err) {
        console.warn('shot reconciliation deferred', err);
      }
    };
    const onOnline = () => void reconcile();
    if (navigator.onLine) void reconcile();
    window.addEventListener('online', onOnline);
    return () => {
      active = false;
      sub.unsubscribe();
      window.removeEventListener('online', onOnline);
    };
  }, [assignmentId]);

  // An expired login pauses the whole outbox (a 401 in lib/sync.ts) and
  // makes direct calls throw UnauthenticatedError (REF-10). Both show the
  // sign-in hint; nothing queued is dropped.
  const [queuePausedByAuth, setQueuePausedByAuth] = useState(false);
  const [authFailed, setAuthFailedState] = useState(false);
  // Read by Bestätigen right after its own flush hit the 401.
  const authFailedRef = useRef(false);
  const setAuthFailed = (v: boolean) => {
    authFailedRef.current = v;
    setAuthFailedState(v);
  };
  const [signingIn, setSigningIn] = useState(false);
  useEffect(() => {
    const sub = liveQuery(() =>
      getLocalDb()
        .outbox.where('status')
        .anyOf('pending', 'inflight')
        .filter((e) => e.lastError === AUTH_PAUSE_ERROR)
        .count(),
    ).subscribe({
      next: (n) => setQueuePausedByAuth(n > 0),
      error: (err) => console.error('auth pause check failed', err),
    });
    return () => sub.unsubscribe();
  }, []);
  const authExpired = queuePausedByAuth || authFailed;

  const refreshLocal = async () => {
    wakeRefereeSync();
  };

  // Latest round timing saved from this screen (TimingPad, SG run). Feeds
  // the pad (REF-7) and the IPSC keypad's HF preview / payload (REF-8).
  const [latestTiming, setLatestTiming] = useState<{
    firstShotTimeMs?: number;
    elapsedTimeMs?: number;
  }>({});

  // Elapsed time saved during this screen's life — read by Bestätigen right
  // after its own flush (state from that flush isn't visible there yet).
  const savedElapsedRef = useRef<number | null>(null);
  useEffect(() => {
    savedElapsedRef.current = null;
  }, [assignmentId]);

  // EVERY timing save (timer import, TimingPad fields, TimingPad stopwatch /
  // SG run) goes through `saveTiming`, which tracks it until settled: some
  // run in the background and may go straight to the server, so Bestätigen
  // must wait for them or the lock can overtake the time (round_locked / old
  // time frozen). `awaited` = Bestätigen reports the failure instead of the
  // caller. The sends themselves are serialized per round
  // (`saveRoundTimingInOrder`: one at a time, waiting ones coalesced), so an
  // older save can't land after a newer correction; settling happens in
  // submit order, so `latestTiming` ends on the last SUBMITTED values.
  type SaveMeta = { awaited: boolean; settled: boolean; err: unknown };
  const timingSaves = useRef(new Map<Promise<unknown>, SaveMeta>());
  async function saveTimingNow(patch: TimingPatch) {
    const res = await saveRoundTimingInOrder(assignmentId, patch);
    // Expired login: the value waits in the queue until the new sign-in.
    if (res.authExpired) setAuthFailed(true);
    const sent = res.sent;
    setLatestTiming((prev) => ({ ...prev, ...sent }));
    if (sent.elapsedTimeMs !== undefined) savedElapsedRef.current = sent.elapsedTimeMs;
  }
  function trackTimingSave(patch: TimingPatch): { done: Promise<void>; meta: SaveMeta } {
    const meta: SaveMeta = { awaited: false, settled: false, err: null };
    const p: Promise<unknown> = saveTimingNow(patch).then(
      () => null,
      (err: unknown) => err ?? new Error('timing_save_failed'),
    );
    timingSaves.current.set(p, meta);
    const done = p.then((err) => {
      meta.settled = true;
      meta.err = err;
      timingSaves.current.delete(p);
      if (err != null) throw err;
    });
    return { done, meta };
  }
  /** TimingPad: rejects on failure (the pad shows its own error). */
  function saveTiming(patch: TimingPatch): Promise<void> {
    return trackTimingSave(patch).done;
  }

  /** Waits (bounded) for all timing saves; false = don't confirm (alert shown). */
  async function awaitTimingSaves(): Promise<boolean> {
    const pending = [...timingSaves.current.entries()];
    if (pending.length === 0) return true;
    for (const [, m] of pending) m.awaited = true;
    await Promise.race([
      Promise.all(pending.map(([p]) => p)),
      new Promise((resolve) => window.setTimeout(resolve, 4_000)),
    ]);
    const failed = pending.filter(([, m]) => m.settled && m.err != null);
    const open = pending.filter(([, m]) => !m.settled);
    // Still running after the bound: their own alert reports a later failure.
    for (const [, m] of open) m.awaited = false;
    if (failed.length > 0) {
      alert(
        `Nicht bestätigt – die Zeit wurde vom Server abgelehnt:\n\n${failed
          .map(([, m]) => `• Zeit: ${errorText(m.err)}`)
          .join('\n')}\n\nBitte die Zeit prüfen und erneut bestätigen.`,
      );
      return false;
    }
    if (open.length > 0) {
      alert('Die Zeit wird noch gespeichert. Bitte gleich erneut bestätigen.');
      return false;
    }
    return true;
  }

  // PAR auto-DQs in flight (sent directly or being queued). Bestätigen waits
  // for them (bounded) so a confirm never overtakes the DQ.
  const dqSaves = useRef(new Set<Promise<void>>());
  function trackDqSave(p: Promise<void>) {
    dqSaves.current.add(p);
    void p.finally(() => dqSaves.current.delete(p));
  }
  /** Waits (bounded) for in-flight DQs; false = don't confirm (alert shown). */
  async function awaitDqSaves(): Promise<boolean> {
    const pending = [...dqSaves.current];
    if (pending.length === 0) return true;
    let settled = false;
    await Promise.race([
      Promise.all(pending).then(() => {
        settled = true;
      }),
      new Promise((resolve) => window.setTimeout(resolve, 4_000)),
    ]);
    if (!settled) {
      alert('Die Disqualifikation wird noch gespeichert. Bitte gleich erneut bestätigen.');
      return false;
    }
    return true;
  }

  // Set right after a successful Bestätigen so the screen flips to the
  // read-only "Runde bestätigt" view without waiting for the roster
  // refetch to deliver `completedAt`.
  const [localCompletedAt, setLocalCompletedAt] = useState<string | null>(null);
  // The confirmation is queued (offline, REF-3) and not on the server yet.
  const [confirmQueued, setConfirmQueued] = useState(false);
  // Unsent timing changes of this round win over the server value in the
  // props — else a remount would show the old time and re-save it on flush.
  // `null` while loading (the pad mounts after, it reads its initial once).
  // The key remounts the pad after a discard.
  const [timingPadKey, setTimingPadKey] = useState(0);
  const [queuedTiming, setQueuedTiming] = useState<{
    firstShotTimeMs?: number;
    elapsedTimeMs?: number;
  } | null>(null);
  useEffect(() => {
    let alive = true;
    setQueuedTiming(null);
    getLocalDb()
      .outbox.where('status')
      .anyOf('pending', 'inflight')
      .filter((e) => e.op === 'round.timing' && assignmentOf(e) === assignmentId)
      .toArray()
      .then((rows) => {
        if (!alive) return;
        const merged: { firstShotTimeMs?: number; elapsedTimeMs?: number } = {};
        for (const e of rows.sort((a, b) => a.createdAt - b.createdAt)) {
          const p = e.payload as { firstShotTimeMs?: number; elapsedTimeMs?: number };
          if (p.firstShotTimeMs !== undefined) merged.firstShotTimeMs = p.firstShotTimeMs;
          if (p.elapsedTimeMs !== undefined) merged.elapsedTimeMs = p.elapsedTimeMs;
        }
        setQueuedTiming(merged);
      })
      .catch(() => alive && setQueuedTiming({}));
    return () => {
      alive = false;
    };
  }, [assignmentId]);
  // Restore that state from the queue: the screen remounts (e.g. after the
  // timer screen) while the server still says "open".
  useEffect(() => {
    const sub = liveQuery(() =>
      getLocalDb()
        .outbox.where('status')
        .anyOf('pending', 'inflight')
        .filter((e) => e.op === 'round.complete' && assignmentOf(e) === assignmentId)
        .count(),
    ).subscribe({
      next: (n) => {
        setConfirmQueued(n > 0);
        if (n > 0) setLocalCompletedAt((prev) => prev ?? new Date().toISOString());
      },
      error: (err) => console.error('queued confirm check failed', err),
    });
    return () => sub.unsubscribe();
  }, [assignmentId]);

  // Bestätigen only confirms + locks this shooter's round. It no longer
  // advances: the referee calls the next shooter explicitly ("📣
  // Aufrufen"). When nobody is left, `lastInRound` goes on to the
  // round-complete screen ("Runde bestätigen — Abschluss", REF-16).
  async function confirmRoundOnly(lastInRound: boolean) {
    if (confirmingRef.current) return;
    confirmingRef.current = true;
    setConfirming(true);
    try {
      // Order matters:
      //   1. Flush any unsaved TimingPad input (user typed but never blurred).
      //   2. Drain the shot outbox (queued shots land on the assignment).
      //   3. confirmRound — locks the assignment from further mutation.
      // Reversing 1+2 vs 3 lets the timing/shots race the lock and silently
      // fail with 400 round_locked once the assignment is confirmed.
      await timingRef.current?.flush();
      // ...and every background timing save too (timer import, stopwatch).
      if (!(await awaitTimingSaves())) return;
      // A PAR auto-DQ still being sent must be on the server or queued
      // before the confirm — else the round locks without the DQ.
      if (!(await awaitDqSaves())) return;
      // Speed without a total time has no Hit Factor — ask before locking.
      if (
        scoringMode === 'speed' &&
        !dq &&
        (savedElapsedRef.current ?? timingElapsedMs) == null &&
        !window.confirm(
          'Speed-Runde ohne Zeit bestätigen? Ohne Gesamtzeit gibt es keinen Hit Factor.',
        )
      )
        return;
      // Bounded: on a dead network a pass can stall up to its 15 s timeout;
      // whatever is still queued afterwards gets the confirm queued behind it.
      await Promise.race([
        refereeDrainer.drain(),
        new Promise((resolve) => window.setTimeout(resolve, 4_000)),
      ]);
      // One read of everything unresolved: the drain may still be moving
      // entries between statuses (the wait above is bounded), and separate
      // counts could miss an entry in flight.
      const unresolved = await getLocalDb()
        .outbox.where('status')
        .anyOf('pending', 'inflight', 'failed_terminal')
        .filter((e) => assignmentOf(e) === assignmentId)
        .toArray();
      // Rejected changes need the referee's decision (retry / discard below)
      // before the round may be locked.
      const rejected = unresolved.filter((e) => e.status === 'failed_terminal');
      if (rejected.length > 0) {
        alert(
          `${rejected.length} Änderung(en) dieser Runde wurden vom Server abgelehnt:\n\n${rejected
            .map((e) => `• ${failedReason(e, shots)}`)
            .join('\n')}\n\nBitte unten erneut versuchen oder verwerfen.`,
        );
        return;
      }
      // Expired login (REF-10): the queue is paused by a 401. Don't confirm
      // (or queue the confirmation) — ask for the new sign-in instead; the
      // queued changes stay and go out after it.
      if (authFailedRef.current || unresolved.some((e) => e.lastError === AUTH_PAUSE_ERROR)) {
        setAuthFailed(true);
        return;
      }
      // Offline (REF-3): shots still queued, or the confirm itself can't
      // reach the server → queue the confirmation behind them (FIFO).
      let queued = unresolved.length > 0;
      if (!queued) {
        try {
          await api.confirmRound(assignmentId);
        } catch (err) {
          if (err instanceof UnauthenticatedError) {
            setAuthFailed(true);
            return;
          }
          if (!isOffline(err)) throw err;
          queued = true;
        }
      }
      if (queued) await queueRoundOp('round.complete', { roundAssignmentId: assignmentId });
      setConfirmQueued(queued);
      if (lastInRound) {
        onRoundComplete();
      } else {
        setLocalCompletedAt(new Date().toISOString());
        onRefetchRoster?.();
      }
    } catch (err) {
      console.error('confirmRound failed', err);
      if (err instanceof UnauthenticatedError) setAuthFailed(true);
      else alert(`Bestätigung fehlgeschlagen: ${errorText(err)}`);
    } finally {
      confirmingRef.current = false;
      setConfirming(false);
    }
  }

  const [callingNext, setCallingNext] = useState(false);
  async function callNext() {
    if (callingNext) return;
    setCallingNext(true);
    try {
      // App handles errors itself (falls back to the roster view).
      await onCallNext();
    } finally {
      setCallingNext(false);
    }
  }

  // Standby (= "timer start" announcement). Local mirror of the server
  // `standby_at` so the chip flips immediately; re-syncs on refetch. A
  // paired SG timer arming/starting sets standby server-side via
  // timer.tick — mirror that locally so the UI doesn't lag the refetch.
  const [standbyLocal, setStandbyLocal] = useState<string | null>(standbyAt);
  useEffect(() => {
    setStandbyLocal(standbyAt);
  }, [standbyAt, assignmentId]);
  const timerLive =
    timerConnected && (timerStatus.state === 'armed' || timerStatus.state === 'running');
  useEffect(() => {
    if (timerLive) setStandbyLocal((prev) => prev ?? new Date().toISOString());
  }, [timerLive]);
  const isStandby = standbyLocal != null || timerLive;

  // REF-18: this screen was just opened by a roster tap — offer
  // "{Name} aufgerufen – Rückgängig" for 10 s from the call, until the
  // shooter has begun (standby / timer / shot).
  const [rosterCall, setRosterCall] = useState(() => {
    const c = getLastRosterCall();
    return c && c.assignmentId === assignmentId && Date.now() - c.at < ROSTER_UNDO_MS ? c : null;
  });
  useEffect(() => {
    if (!rosterCall) return;
    const t = window.setTimeout(
      () => setRosterCall(null),
      Math.max(0, rosterCall.at + ROSTER_UNDO_MS - Date.now()),
    );
    return () => window.clearTimeout(t);
  }, [rosterCall]);
  const [uncalling, setUncalling] = useState(false);
  async function uncall() {
    if (!rosterCall || uncalling) return;
    setUncalling(true);
    try {
      await api.uncallShooter(assignmentId);
      // Used up: the roster must not offer it again.
      setLastRosterCall(null);
      setRosterCall(null);
      onBack();
    } catch (err) {
      if (err instanceof UnauthenticatedError) setAuthFailed(true);
      else if (isOffline(err)) alert('Offline — Rückgängig geht nur mit Verbindung.');
      else alert(errorText(err));
    } finally {
      setUncalling(false);
    }
  }
  const [standbyBusy, setStandbyBusy] = useState(false);
  const [standbyOffline, setStandbyOffline] = useState(false);
  // Standby also starts a paired SG timer with the RANDOM start delay
  // (1-4 s), so the shooter can't anticipate the beep. Without a connected
  // timer it only sets standby. "▶ Timer Start" stays as the fallback
  // (e.g. a re-run) and keeps the configured fixed delay.
  const canStartTimerOnStandby =
    timerConnected &&
    !timerStatus.disconnected &&
    timerStatus.state !== 'armed' &&
    timerStatus.state !== 'running';
  async function toggleStandby(standby: boolean) {
    if (standbyBusy) return;
    setStandbyBusy(true);
    try {
      await api.setStandby(assignmentId, standby);
      setStandbyOffline(false);
    } catch (err) {
      // Offline (REF-3): carry on locally and start the timer anyway. Not
      // queued — a Standby arriving after the run would show wrongly on
      // the boards.
      if (!isOffline(err)) {
        console.error('setStandby failed', err);
        alert('Standby fehlgeschlagen — bitte erneut versuchen.');
        setStandbyBusy(false);
        return;
      }
      setStandbyOffline(standby);
    }
    setStandbyLocal(standby ? new Date().toISOString() : null);
    if (standby && canStartTimerOnStandby) {
      try {
        // Same steps as the timer bar's Start: arm the round for the
        // camera first, then start; claim only after start() reset the
        // previous run's shots.
        if (!armedAt) await armRound(true);
        await timerSource.start({ startDelayDS: RANDOM_START_DELAY_DS });
        claimRun(assignmentId);
      } catch (err) {
        console.error('timer start on standby failed', err);
        alert('Standby gesetzt, aber der SG Timer startete nicht — bitte „▶ Timer Start" drücken.');
      }
    }
    setStandbyBusy(false);
  }

  async function retrySync() {
    await retryAssignment(assignmentId);
    wakeRefereeSync();
  }

  async function discardSync() {
    if (
      !window.confirm(
        'Alle ungespeicherten Änderungen dieser Runde verwerfen und den Serverstand laden?',
      )
    )
      return;
    try {
      await reconcileAssignment(
        assignmentId,
        async (id) => {
          const params = new URLSearchParams({
            input: JSON.stringify({ assignmentId: id }),
            batch: '0',
          });
          const res = await fetch(`/api/trpc/shots.listByAssignment?${params}`, {
            credentials: 'include',
            signal: AbortSignal.timeout(15_000),
          });
          if (!res.ok) throw new Error(`shots.list: HTTP ${res.status}`);
          const body = await res.json();
          if (!Array.isArray(body.result?.data)) throw new Error('invalid shot list');
          return body.result.data;
        },
        undefined,
        { discardQueued: true },
      );
      // A discarded queued confirmation leaves the round open on the server,
      // and discarded timing must not stay in the pad (flush would re-save
      // it): remount the pad from the server values and reload them.
      setLocalCompletedAt(null);
      setConfirmQueued(false);
      setQueuedTiming({});
      setLatestTiming({});
      savedElapsedRef.current = null;
      wakeRefereeSync();
      // Discarded penalty entries: show the server's penalties again.
      void penaltyView.refresh();
      await onRefetchRoster?.();
      setTimingPadKey((k) => k + 1);
    } catch {
      alert('Serverstand konnte nicht geladen werden. Änderungen wurden nicht verworfen.');
    }
  }

  // DQ reason sheet (REF-20) instead of window.prompt.
  const [dqSheetOpen, setDqSheetOpen] = useState(false);
  // Set when this screen's PAR auto-DQ was saved — bridges the moment until
  // the roster refetch delivers the server's `dqReason`. Reset per shooter.
  const [parAutoDqLocal, setParAutoDqLocal] = useState(false);
  useEffect(() => {
    setParAutoDqLocal(false);
  }, [assignmentId]);
  async function disqualify(reason: string) {
    try {
      // Offline / behind unsent changes / expired login: queued (`round.dq`),
      // FIFO before a later confirm; the screen shows it as DQ'd at once.
      const res = await disqualifyRoundOrQueue(assignmentId, { reason, terminal: false });
      setDqSheetOpen(false);
      if (res.authExpired) setAuthFailed(true);
      if (!res.queued) onRefetchRoster?.();
    } catch (err) {
      console.error('disqualifyRound failed', err);
      if (err instanceof UnauthenticatedError) {
        setDqSheetOpen(false);
        setAuthFailed(true);
      } else alert(`Disqualifikation fehlgeschlagen: ${errorText(err)}`);
    }
  }

  // Recapture the kiosk's slot-B "before" snapshot. Server enforces
  // that the timer hasn't started AND no shots are logged; the button
  // mirrors that gate locally so it only shows up while it's safe to
  // call. UI is fire-and-forget — the SSE fan-out updates kiosks; the
  // referee tablet only surfaces errors.
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  // "Mehr …" toggle for the secondary pre-run actions (REF-13).
  const [moreOpen, setMoreOpen] = useState(false);
  async function recaptureBeforeSnapshot() {
    setSnapshotBusy(true);
    try {
      await api.captureRoundSnapshot({
        roundAssignmentId: assignmentId,
        kind: 'before',
        force: true,
      });
    } catch (err) {
      console.error('captureRoundSnapshot failed', err);
      const msg =
        errorCode(err) === 'shots_already_logged' || errorCode(err) === 'shooter_already_started'
          ? 'Zu spät — Schuss oder Timer-Start bereits erfasst. Vorher-Bild kann nur vor dem ersten Schuss neu aufgenommen werden.'
          : 'Schnappschuss fehlgeschlagen — bitte erneut versuchen.';
      alert(msg);
    } finally {
      setSnapshotBusy(false);
    }
  }

  const effectiveCompletedAt = completedAt ?? localCompletedAt;
  const locked = effectiveCompletedAt != null;
  // A queued (offline) DQ counts at once; its reason until the server's.
  const dq = disqualifiedAt != null || queuedDq != null || landedDq != null;
  const shownDqReason = dqReason ?? queuedDq?.reason ?? landedDq ?? null;
  const showUncall = rosterCall != null && !isStandby && shots.length === 0 && !locked && !dq;
  const done = shots.length >= shotCount;
  // hart-offen: a PAR auto-DQ does NOT tear down recording — the timer keeps
  // capturing shot timings and the referee keeps scoring until the shot count
  // is reached (or the 2×PAR safety bound), so a later web-UI DQ-revert has
  // the true timings. hart-stopp and manual DQs collapse the UI as before.
  // "Auto-DQ" = the server's stored reason is the auto-DQ text (survives a
  // refetch/remount), or this screen just fired it and the refetch hasn't
  // delivered the reason yet. Variable-shot stages (shotCount 0 ⇒ done) have
  // no fixed count to record to, so they collapse too.
  const parAutoDq =
    dq && (shownDqReason === PAR_AUTO_DQ_REASON || (parAutoDqLocal && shownDqReason == null));
  const keepRecordingThroughDq = parAutoDq && !locked && !done && effectiveParMode === 'hard';
  // Render the timer bar / keypad / timing pad either for a normal round or
  // while a hart-offen DQ is still recording.
  const showRecordingUi = !dq || keepRecordingThroughDq;
  // Cumulative magazine boundaries (excluding the final magazine end) —
  // the shot counts at which a magazine empties and the shooter must
  // reload. Empty when there's no breakdown. "Reload due" = the recorded
  // shots exactly fill a magazine and the round isn't finished.
  const magBoundaries: number[] = [];
  if (magazines && magazines.length > 1) {
    let acc = 0;
    for (let i = 0; i < magazines.length - 1; i += 1) {
      acc += magazines[i]!.shots;
      magBoundaries.push(acc);
    }
  }
  const reloadDue = !dq && !locked && !done && magBoundaries.includes(shots.length);
  const reloadMagNo = reloadDue ? magBoundaries.indexOf(shots.length) + 1 : 0;
  // The slot-B "before" recapture button (below the Arm row) is only
  // safe to expose before any shot has landed and before the timer
  // armed — mirrors the server-side guard so we don't surface a tap
  // that would just bounce.
  const canRecaptureBefore = !dq && !locked && armedAt == null && shots.length === 0;

  // Sticky bottom action bar (UX review REF-12): "Aufrufen" on the locked
  // view, "Bestätigen" once the round is done. The PiP overlays move up
  // above it so they don't cover the button.
  // A DQ'd round can always be confirmed — also while a hart-offen auto-DQ
  // is still recording, even with fewer shots than the count.
  const confirmReady = done || dq;
  const stickyBar = locked ? nextShooterLabel != null : confirmReady;
  const pipBottom = stickyBar ? 'bottom-28' : 'bottom-3';

  // Floating picture-in-picture copy of the hit indicator. Rendered
  // out-of-flow at the bottom-right; works alongside both the locked
  // confirmation view and the active keypad. When PiP is on, the
  // inline copies in the keypad / locked summary suppress themselves
  // (via `hideHitIndicator` / the `!hitPip` guard) so the indicator
  // shows exactly once. We also show the empty target before any shot
  // lands — gives the referee visual confirmation that PiP is on.
  const hitPipOverlay =
    hitPip && !dq ? (
      <div
        className={`fixed right-3 ${pipBottom} z-40 w-44 rounded-lg border border-zinc-700 bg-zinc-900/95 p-2 shadow-xl sm:w-56`}
      >
        <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-zinc-500">
          <span>Treffer</span>
          <button
            onClick={() => setHitPip(false)}
            title="PiP schließen — Trefferanzeige inline anzeigen"
            aria-label="Trefferanzeige inline anzeigen"
            className="min-h-12 min-w-12 rounded px-1 text-zinc-400 hover:text-emerald-300"
          >
            ×
          </button>
        </div>
        <HitIndicator
          shots={shots}
          shotCount={shotCount}
          schemaKind={scoringSchemaKind}
          zones={scoringSchemaZones}
          targetZones={targetZones as TargetZone[]}
          shotPositions={shotPositions}
          pendingDetections={pendingDetections}
          penalties={penaltyEntries}
          onConfirmDetection={(id) => void handleConfirmDetection(id)}
          onDismissDetection={(id) => void handleDismissDetection(id)}
        />
      </div>
    ) : null;

  // Floating camera tile mirroring the public scoreboard's PiP. Uses
  // the shared CameraPanel in `inline` mode wrapped in our own fixed
  // container so the close button can capture pointer events (the
  // built-in `overlay` mode disables pointer events on the entire
  // tile). Renders only while `streamPip` is on AND streamMeta has
  // resolved — until then there's nothing useful to show.
  // CameraPanel sizes the inline tile from `meta.displayPct` (the
  // admin-set "prominent column" knob, typically 30–50). On the
  // referee tablet that's enormous — we want a small thumb-sized PiP
  // that doesn't cover the keypad. Override displayPct to a fixed
  // small value just for this tile; the board still gets the admin's
  // value because it consumes the original meta directly.
  const streamMetaPip = useMemo(
    () => (streamMeta ? { ...streamMeta, displayPct: 22 } : null),
    [streamMeta],
  );

  const streamPipOverlay =
    streamPip && !dq && streamMetaPip ? (
      <div
        className={`fixed right-3 ${pipBottom} z-40 rounded-lg border border-zinc-700 bg-zinc-900/95 p-1 shadow-xl`}
      >
        <div className="mb-1 flex items-center justify-between px-1 text-[10px] uppercase tracking-wider text-zinc-500">
          <span>Kamera</span>
          <button
            onClick={() => setStreamPip(false)}
            title="PiP schließen"
            aria-label="Kamera-PiP schließen"
            className="min-h-12 min-w-12 rounded px-1 text-zinc-400 hover:text-emerald-300"
          >
            ×
          </button>
        </div>
        <CameraPanel meta={streamMetaPip} mode="inline" detections={numberedDetections} />
      </div>
    ) : null;

  // Speed mode REQUIRES timing to compute hitFactor; precision mode
  // records it as optional metadata. We always show the pad — it's
  // tiny, it's collapsible, and any leaderboard view can pick up the
  // data even on a plain precision round.
  const timingRequired = scoringMode === 'speed';
  // Current round timing: saved here > still queued > server value.
  const timingFirstShotMs =
    latestTiming.firstShotTimeMs ?? queuedTiming?.firstShotTimeMs ?? initialFirstShotMs;
  const timingElapsedMs =
    latestTiming.elapsedTimeMs ?? queuedTiming?.elapsedTimeMs ?? initialElapsedMs;
  const distanceLabel = (() => {
    const m = distanceCm / 100;
    return `${m.toLocaleString('de-AT', { maximumFractionDigits: 2 })} m`;
  })();
  const modeLabel = scoringMode === 'speed' ? 'Speed' : 'Präzision';
  const disciplineLabel = disciplineCode
    ? (DISCIPLINE_LABEL[disciplineCode] ?? disciplineCode)
    : null;

  // Amber "N ausstehend" like the header pill in App.tsx (REF-9).
  const pendingBadge =
    pending > 0 ? (
      <span
        role="status"
        className="rounded-full border border-amber-600 px-2 py-0.5 text-xs font-semibold text-amber-300"
      >
        {pending} ausstehend
      </span>
    ) : null;

  // Expired login (REF-10): hint + the same inline sign-in as the roster.
  const authBanner = !authExpired ? null : signingIn ? (
    <div className="mb-3">
      <SignInScreen
        onSignedIn={() => {
          setSigningIn(false);
          setAuthFailed(false);
          void resumeAfterSignIn();
        }}
      />
      <button
        type="button"
        onClick={() => setSigningIn(false)}
        className="mt-2 min-h-12 text-sm text-zinc-400 underline hover:text-zinc-200"
      >
        Abbrechen
      </button>
    </div>
  ) : (
    <div
      role="alert"
      className="mb-3 rounded-lg border border-amber-600 bg-amber-950/40 p-3 text-sm text-amber-200"
    >
      <p>Anmeldung abgelaufen – bitte neu anmelden. Deine Eingaben bleiben gespeichert.</p>
      <button
        type="button"
        onClick={() => setSigningIn(true)}
        className="mt-2 min-h-12 rounded-lg bg-amber-600 px-4 py-2 font-bold text-white hover:bg-amber-500"
      >
        Neu anmelden
      </button>
    </div>
  );

  if (locked) {
    return (
      <section>
        {authBanner}
        <div className="mb-4 flex items-end justify-between">
          <div>
            <p className="text-xs text-zinc-500">{shooterLabel}</p>
            <h2 className="text-2xl font-bold">
              {dq ? '❌ Disqualifiziert' : '🔒 Runde bestätigt'}
            </h2>
            {dq && shownDqReason && (
              <p className="mt-1 text-sm font-medium text-red-300">Grund: {shownDqReason}</p>
            )}
            {confirmQueued && !authExpired && (
              <p className="mt-1 text-sm text-amber-300">
                Offline — die Bestätigung wird übertragen, sobald wieder Verbindung besteht.
              </p>
            )}
            {pendingBadge && <p className="mt-1">{pendingBadge}</p>}
            <p className="text-xs text-zinc-500">
              Bestätigt am{' '}
              {new Date(effectiveCompletedAt).toLocaleString('de-AT', {
                timeZone: 'Europe/Vienna',
              })}
              . Korrekturen sind nur über den Admin-Bereich („Runde wieder öffnen") möglich.
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold tabular-nums">
              {dq
                ? 0
                : roundPoints(
                    scoringSchemaKind,
                    shots,
                    [{ points: penaltyView.total }],
                    scoringSchemaKind === 'hit_factor' ? ipscPoints(scoringSchemaConfig) : null,
                  )}
            </div>
            {!dq && penaltyView.rows.length > 0 && (
              <p className="text-xs tabular-nums text-orange-300">
                inkl. Strafen {formatPenaltyPoints(penaltyView.total)}
              </p>
            )}
          </div>
        </div>
        {!dq && (
          <div className="mb-3 flex flex-wrap justify-end gap-2">
            <button
              onClick={() => setHitPip((p) => !p)}
              title={
                hitPip
                  ? 'Trefferanzeige inline anzeigen'
                  : 'Trefferanzeige als Bild-im-Bild ausklappen'
              }
              aria-pressed={hitPip ? 'true' : 'false'}
              className={`min-h-12 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                hitPip
                  ? 'border-emerald-500 bg-emerald-700 text-white'
                  : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-500'
              }`}
            >
              {hitPip ? '🪟 PiP an' : '🪟 Trefferanzeige als PiP'}
            </button>
            <button
              onClick={() => setStreamPip((p) => !p)}
              title={
                streamPip
                  ? 'Kamera-PiP schließen'
                  : 'Live-Kamera als Bild-im-Bild — weckt die CV-Erkennung'
              }
              aria-pressed={streamPip ? 'true' : 'false'}
              className={`min-h-12 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                streamPip
                  ? 'border-amber-400 bg-amber-600 text-white'
                  : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-500'
              }`}
            >
              {streamPip ? '📹 Kamera an' : '📹 Kamera als PiP'}
            </button>
          </div>
        )}
        {!dq && shots.length > 0 && !hitPip && (
          <div className="mb-4">
            <HitIndicator
              shots={shots}
              shotCount={shotCount}
              schemaKind={scoringSchemaKind}
              zones={scoringSchemaZones}
              targetZones={targetZones as TargetZone[]}
              shotPositions={shotPositions}
              pendingDetections={pendingDetections}
              penalties={penaltyEntries}
              onConfirmDetection={(id) => void handleConfirmDetection(id)}
              onDismissDetection={(id) => void handleDismissDetection(id)}
            />
          </div>
        )}
        <ul className="grid grid-cols-5 gap-2">
          {shots.map((s) => (
            <li
              key={s.id}
              className="relative rounded border border-zinc-800 bg-zinc-900 px-3 py-2 text-center text-xl font-bold text-zinc-300"
            >
              <span className="text-xs text-zinc-500">#{s.ord}</span>
              <br />
              {s.valueNumeric ?? s.zoneCode ?? '–'}
              {unsentKeys.has(s.clientIdempotencyKey) && (
                <>
                  <span
                    aria-hidden
                    className="absolute right-1 top-1 h-2 w-2 rounded-full bg-amber-400"
                  />
                  <span className="sr-only"> noch nicht gesendet</span>
                </>
              )}
            </li>
          ))}
        </ul>
        <PenaltySection
          assignmentId={assignmentId}
          shooterLabel={shooterLabel}
          rows={penaltyView.rows}
          total={penaltyView.total}
          values={penaltyValues}
          editable={false}
          onChanged={penaltyView.refresh}
          onAuthExpired={() => setAuthFailed(true)}
        />
        {/* Direct child of the full-height screen: `sticky` can't leave its
            parent, so inside the short footer it would never stick. */}
        {nextShooterLabel && (
          <div className={`mt-6 ${STICKY_BAR}`}>
            {nextShooterNotice && (
              <p role="status" className="mb-2 text-sm text-amber-300">
                {nextShooterNotice}
              </p>
            )}
            <button
              onClick={() => void callNext()}
              disabled={callingNext}
              className="w-full rounded-lg bg-emerald-700 px-4 py-5 text-left text-white hover:bg-emerald-600 active:bg-emerald-800 disabled:opacity-50"
            >
              <span className="block text-xs font-medium uppercase tracking-wider text-emerald-200">
                {t('shooter')} bereit?
              </span>
              <span className="block truncate text-xl font-bold">
                {callingNext ? 'Wird aufgerufen…' : `📣 Aufrufen: ${nextShooterLabel}`}
              </span>
            </button>
          </div>
        )}
        <div className="mt-6 flex flex-col gap-2">
          <button
            onClick={onBack}
            className={
              nextShooterLabel
                ? 'rounded border border-zinc-700 px-4 py-3 text-sm text-zinc-300 hover:border-zinc-500'
                : 'rounded-lg bg-emerald-700 px-4 py-4 text-lg font-bold text-white hover:bg-emerald-600'
            }
          >
            ← Übersicht
          </button>
          {failed > 0 && (
            <button
              onClick={() => void retrySync()}
              className="min-h-12 rounded border border-amber-700 px-4 py-2 text-sm text-amber-200 hover:bg-amber-900/30"
            >
              {failed} fehlgeschlagen — erneut versuchen
            </button>
          )}
          {failed > 0 && (
            <button
              onClick={() => void discardSync()}
              className="min-h-12 text-amber-400 underline"
            >
              Ungespeicherte Änderungen verwerfen
            </button>
          )}
          <FailedReasons reasons={failedReasons} />
        </div>
        {hitPipOverlay}
        {streamPipOverlay}
      </section>
    );
  }

  return (
    <div>
      {authBanner}
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
        <span
          className={`rounded-full border px-2 py-0.5 font-medium ${
            scoringMode === 'speed'
              ? 'border-amber-500 bg-amber-900/30 text-amber-200'
              : 'border-emerald-600 bg-emerald-900/30 text-emerald-200'
          }`}
        >
          {modeLabel}
        </span>
        <span className="rounded-full border border-zinc-700 bg-zinc-900 px-2 py-0.5 font-medium text-zinc-300">
          {distanceLabel}
        </span>
        {disciplineLabel && (
          <span className="rounded-full border border-sky-700 bg-sky-900/30 px-2 py-0.5 font-medium text-sky-200">
            {disciplineLabel}
          </span>
        )}
        {scenarioTitle && (
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500 bg-emerald-900/40 px-2 py-0.5 font-semibold text-emerald-200">
            <span aria-hidden>◉</span> Szenario: {scenarioTitle}
            <span className="ml-1 font-mono tabular-nums">
              {formatCountUp(scenarioTimer.elapsedMs)}
            </span>
          </span>
        )}
        {pendingBadge}
      </div>
      {calibrationGap.needed && (
        <div
          role="status"
          className="mb-3 flex items-start gap-2 rounded-lg border-2 border-amber-500 bg-amber-500/15 p-3 text-sm text-amber-100"
        >
          <span aria-hidden className="text-lg leading-none">
            📷⚠
          </span>
          <span>
            <span className="font-semibold">
              Scheibe {calibrationGap.targetName ? `„${calibrationGap.targetName}“ ` : ''}
              hier nicht kalibriert
            </span>{' '}
            — keine Kamera-Treffererkennung in dieser Runde. Werten geht ganz normal.
          </span>
        </div>
      )}
      {!dq && shots.length === 0 && (
        <div className="mb-3 rounded-lg border border-zinc-800 bg-zinc-900/60 p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="truncate text-sm font-semibold text-zinc-200">{shooterLabel}</span>
            <span
              title={
                calledAt
                  ? `Aufgerufen um ${new Date(calledAt).toLocaleTimeString('de-AT', {
                      timeZone: 'Europe/Vienna',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}`
                  : undefined
              }
              role="status"
              className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-semibold ${
                isStandby
                  ? 'border-amber-400 bg-amber-500/20 text-amber-200'
                  : 'border-sky-600 bg-sky-900/30 text-sky-200'
              }`}
            >
              {isStandby ? '⏱ Standby' : '📣 Aufgerufen'}
            </span>
          </div>
          {standbyOffline && isStandby && (
            <p className="mb-2 text-xs text-amber-300">
              Offline — Standby gilt nur hier am Tablet, die Anzeigetafeln zeigen ihn nicht.
            </p>
          )}
          {!isStandby ? (
            <button
              type="button"
              onClick={() => void toggleStandby(true)}
              disabled={standbyBusy}
              className="w-full rounded-lg bg-amber-600 px-4 py-4 text-lg font-bold text-white hover:bg-amber-500 active:bg-amber-700 disabled:opacity-50"
            >
              ⏱ Standby
              <span className="ml-2 text-xs font-normal text-amber-100/90">
                {canStartTimerOnStandby
                  ? '(startet den Timer · Zufallsstart 1–4 s)'
                  : '(Timer-Start ansagen)'}
              </span>
            </button>
          ) : (
            !timerLive && (
              <button
                type="button"
                onClick={() => void toggleStandby(false)}
                disabled={standbyBusy}
                className="min-h-12 text-xs text-zinc-400 underline hover:text-zinc-200 disabled:opacity-50"
              >
                ↶ Standby zurücknehmen
              </button>
            )
          )}
        </div>
      )}
      {/* Secondary pre-run actions behind "Mehr …" (REF-13): Standby stays
          the one big action, "▶ Timer Start" stays in the timer bar. */}
      {((!dq && !armedAt) || canRecaptureBefore) && (
        <div className="mb-3">
          <button
            type="button"
            onClick={() => setMoreOpen((o) => !o)}
            aria-expanded={moreOpen}
            aria-controls="score-more-actions"
            className="flex min-h-12 w-full items-center justify-between rounded-lg border border-zinc-700 px-4 py-2 text-sm text-zinc-300 hover:border-zinc-500"
          >
            <span>Mehr …</span>
            <span aria-hidden>{moreOpen ? '▴' : '▾'}</span>
          </button>
          {moreOpen && (
            <div id="score-more-actions" className="mt-2 space-y-2">
              {!dq && !armedAt && (
                <button
                  type="button"
                  onClick={() => void armRound(false)}
                  className="min-h-12 w-full rounded-lg border border-emerald-700 bg-emerald-900/30 px-4 py-3 text-sm font-bold text-emerald-200 hover:border-emerald-500 hover:bg-emerald-900/50"
                >
                  🎯 Runde scharfschalten
                  <span className="ml-2 text-xs font-normal text-emerald-300/80">
                    (Kamera-Erkennung starten)
                  </span>
                </button>
              )}
              {canRecaptureBefore && (
                <button
                  type="button"
                  onClick={() => void recaptureBeforeSnapshot()}
                  disabled={snapshotBusy}
                  title="Aktuelles Kamerabild als Vorher-Schnappschuss für die Anzeige speichern"
                  className="min-h-12 w-full rounded-lg border border-dashed border-violet-600/70 bg-violet-950/30 px-4 py-2 text-xs font-bold text-violet-200 hover:border-violet-400 hover:bg-violet-950/60 disabled:opacity-50"
                >
                  📸 Vorher-Bild neu aufnehmen
                  <span className="ml-2 font-normal text-violet-300/80">(für die Anzeige)</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}
      {showRecordingUi && (
        <ScoreTimerBar
          sessionId={sessionId}
          assignmentId={assignmentId}
          shooterLabel={shooterLabel}
          shotCount={shotCount}
          scoringSchemaKind={scoringSchemaKind}
          completedAt={completedAt}
          maxRecordingSeconds={maxRecordingSeconds}
          effectiveParSeconds={effectiveParSeconds}
          effectiveParMode={effectiveParMode}
          roundArmedAt={armedAt}
          onAutoArm={() => armRound(true)}
          onOpenTimerDebug={onOpenTimerDebug}
          onTimingsReady={(ts) => {
            const { done, meta } = trackTimingSave(ts);
            done.catch((err: unknown) => {
              console.error('saveTiming (timer) failed', err);
              // Bestätigen is waiting for it and reports the failure itself.
              if (meta.awaited) return;
              alert(`Zeit konnte nicht gespeichert werden: ${errorText(err)}`);
            });
          }}
          onAutoDQ={(res) => {
            setParAutoDqLocal(true);
            if (res.authExpired) setAuthFailed(true);
            if (!res.queued) void onRefetchRoster?.();
          }}
          onAutoDqPending={trackDqSave}
        />
      )}
      {showRecordingUi && pendingDetections.length > 0 && (
        <div className="my-3 rounded-lg border border-emerald-700 bg-emerald-950/30 p-3">
          <p className="text-xs uppercase tracking-wider text-emerald-300">CV-Erkennung</p>
          {pendingDetections.slice(-1).map((d) => (
            <div key={d.id} className="mt-1 flex items-center justify-between gap-2">
              <span className="text-sm text-emerald-100">
                {d.zoneCode ?? `${d.valueNumeric ?? '?'} Ringe`} erkannt
                {d.valueNumeric != null && d.zoneCode == null
                  ? ''
                  : d.valueNumeric != null
                    ? ` · ${d.valueNumeric} Pkt.`
                    : ''}
              </span>
              <span className="flex gap-2">
                <button
                  type="button"
                  onClick={() => void handleConfirmDetection(d.id)}
                  className="min-h-12 rounded-full border border-emerald-400 bg-emerald-700 px-4 py-1 text-xs font-medium text-white hover:bg-emerald-600"
                >
                  Übernehmen
                </button>
                <button
                  type="button"
                  onClick={() => void handleDismissDetection(d.id)}
                  className="min-h-12 rounded-full border border-zinc-600 bg-zinc-800 px-4 py-1 text-xs font-medium text-zinc-200 hover:border-zinc-400"
                >
                  Verwerfen
                </button>
              </span>
            </div>
          ))}
          {pendingDetections.length > 1 && (
            <p className="mt-1 text-[11px] text-emerald-300/70">
              {pendingDetections.length - 1} weitere Erkennung
              {pendingDetections.length - 1 === 1 ? '' : 'en'} ausstehend.
            </p>
          )}
        </div>
      )}
      {reloadDue && (
        <div className="rounded-lg border border-amber-500 bg-amber-500/15 px-4 py-3 text-center text-amber-200">
          <span className="text-lg font-bold">🔄 Nachladen</span>
          <span className="ml-2 text-sm">
            Magazin {reloadMagNo} leer — {shots.length}/{shotCount} Schuss
          </span>
        </div>
      )}
      {dq && (
        <section className="rounded-lg border border-red-700 bg-red-950/40 p-4">
          <p className="text-xs uppercase tracking-wider text-red-300">am Schießstand</p>
          <h2 className="truncate text-3xl font-extrabold leading-tight sm:text-4xl">
            {shooterLabel}
          </h2>
          <p className="mt-3 text-2xl font-bold text-red-300">❌ Disqualifiziert</p>
          {shownDqReason && <p className="mt-1 text-sm text-red-200">Grund: {shownDqReason}</p>}
          {keepRecordingThroughDq ? (
            <p className="mt-2 text-sm font-medium text-amber-200">
              ⏱️ Aufzeichnung läuft weiter — bitte weiter erfassen, bis {shotCount} Schuss erreicht
              sind, damit die Zeiten vollständig bleiben (falls die DQ später aufgehoben wird).
            </p>
          ) : (
            <p className="mt-2 text-xs text-zinc-400">
              Wertung dieser Runde wird mit 0 verrechnet. Erfasste Schüsse bleiben für die
              Audit-Spur erhalten.
            </p>
          )}
          <p className="mt-2 text-xs text-zinc-400">
            DQ aufheben kann nur ein Admin (Web-Admin › Trainings › Training öffnen › „DQ
            aufheben"). Eine DQ durch PAR „hart-stopp" ist endgültig.
          </p>
        </section>
      )}
      <fieldset disabled={confirming}>
        {showRecordingUi &&
          (scoringSchemaKind === 'hit_factor' ? (
            <IpscKeypad
              assignmentId={assignmentId}
              shotCount={shotCount}
              shooterLabel={shooterLabel}
              shots={shots}
              onShot={() => void refreshLocal()}
              schemaZones={scoringSchemaZones}
              schemaConfig={scoringSchemaConfig}
              targetZones={targetZones}
              shotPositions={shotPositions}
              pendingDetections={pendingDetections}
              penalties={penaltyEntries}
              onConfirmDetection={(id) => void handleConfirmDetection(id)}
              onDismissDetection={(id) => void handleDismissDetection(id)}
              hideHitIndicator={hitPip}
              hitPipActive={hitPip}
              onToggleHitPip={() => setHitPip((p) => !p)}
              streamPipActive={streamPip}
              onToggleStreamPip={() => setStreamPip((p) => !p)}
              unsentKeys={unsentKeys}
              penaltyPoints={penaltyView.total}
              firstShotTimeMs={timingFirstShotMs}
              elapsedTimeMs={timingElapsedMs}
              scoringMode={scoringMode}
              disqualified={dq}
            />
          ) : (
            <NumericKeypad
              assignmentId={assignmentId}
              shotCount={shotCount}
              shooterLabel={shooterLabel}
              shots={shots}
              onShot={() => void refreshLocal()}
              schemaKind={scoringSchemaKind}
              schemaZones={scoringSchemaZones}
              targetZones={targetZones}
              shotPositions={shotPositions}
              pendingDetections={pendingDetections}
              penalties={penaltyEntries}
              onConfirmDetection={(id) => void handleConfirmDetection(id)}
              onDismissDetection={(id) => void handleDismissDetection(id)}
              values={
                scoringSchemaZones.length > 0
                  ? scoringSchemaZones
                      .slice()
                      .sort((a, b) => a.ordering - b.ordering)
                      .map((z) => z.valueNumeric)
                  : [10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0]
              }
              hideHitIndicator={hitPip}
              hitPipActive={hitPip}
              onToggleHitPip={() => setHitPip((p) => !p)}
              streamPipActive={streamPip}
              onToggleStreamPip={() => setStreamPip((p) => !p)}
              unsentKeys={unsentKeys}
              penaltyPoints={penaltyView.total}
              elapsedTimeMs={timingElapsedMs}
              scoringMode={scoringMode}
              disqualified={dq}
            />
          ))}
        {/* Same rules as the keypad: editable while the round is open and
            still being scored (not confirmed, no collapsing DQ); read-only
            otherwise. */}
        <PenaltySection
          assignmentId={assignmentId}
          shooterLabel={shooterLabel}
          rows={penaltyView.rows}
          total={penaltyView.total}
          values={penaltyValues}
          editable={showRecordingUi}
          onChanged={penaltyView.refresh}
          onAuthExpired={() => setAuthFailed(true)}
        />
      </fieldset>

      {showRecordingUi && queuedTiming && (
        <fieldset className="mt-4" disabled={confirming}>
          <TimingPad
            key={timingPadKey}
            ref={timingRef}
            initialFirstShotMs={timingFirstShotMs}
            initialElapsedMs={timingElapsedMs}
            onSave={saveTiming}
            required={timingRequired}
            shotCount={shotCount}
            scoringSchemaKind={scoringSchemaKind}
            onOpenTimerDebug={onOpenTimerDebug}
            allowPhoneTimer={allowPhoneTimer}
            assignmentId={assignmentId}
          />
        </fieldset>
      )}

      {/* Direct child of the full-height screen (see the locked view). */}
      {confirmReady && (
        <div className={`mt-6 ${STICKY_BAR}`}>
          <button
            onClick={() => void confirmRoundOnly(!nextShooterLabel)}
            disabled={confirming}
            className="w-full rounded-lg bg-emerald-700 px-4 py-4 text-lg font-bold text-white hover:bg-emerald-600 active:bg-emerald-800 disabled:opacity-50"
          >
            {nextShooterLabel ? '✓ Bestätigen' : '✓ Runde bestätigen — Abschluss'}
          </button>
        </div>
      )}
      <div className="mt-6 flex flex-col gap-2">
        {!done && showRecordingUi && (
          <p className="rounded border border-zinc-800 px-4 py-2 text-center text-xs text-zinc-500">
            Korrekturen sind möglich, bis die Runde bestätigt wird.
          </p>
        )}
        {/* Both before+after present = canonical `pair_ready` status. */}
        {done && !dq && snapshotPair.before && snapshotPair.after && (
          <button
            onClick={() => setShowComparison(true)}
            className="rounded border border-violet-700 bg-violet-950/40 px-4 py-3 text-sm font-semibold text-violet-200 hover:border-violet-500 hover:bg-violet-900/40"
          >
            📸 Schussbild ansehen
          </button>
        )}
        {!dq && (
          <button
            onClick={() => setDqSheetOpen(true)}
            className="rounded border border-red-800 bg-red-950/30 px-4 py-3 text-sm font-bold text-red-300 hover:border-red-500 hover:bg-red-950/50"
          >
            ❌ Disqualifizieren …
          </button>
        )}
        <button
          onClick={onBack}
          className="min-h-12 rounded border border-zinc-700 px-4 py-2 text-sm text-zinc-300 hover:border-emerald-500"
        >
          ← Übersicht (Runde nicht bestätigen)
        </button>
      </div>

      {/* The pending count is the amber badge at the top (REF-9). */}
      {failed > 0 && (
        <p className="mt-3 text-center text-xs text-zinc-500">
          <button
            onClick={() => void retrySync()}
            className="min-h-12 text-amber-400 underline hover:text-amber-300"
          >
            {failed} fehlgeschlagen — erneut versuchen
          </button>
          {' · '}
          <button onClick={() => void discardSync()} className="min-h-12 text-amber-400 underline">
            Ungespeicherte Änderungen verwerfen
          </button>
        </p>
      )}
      <FailedReasons reasons={failedReasons} />
      {hitPipOverlay}
      {streamPipOverlay}
      {showUncall && rosterCall && (
        <div role="status" className="fixed inset-x-0 bottom-0 z-40 flex justify-center p-4">
          <div className="flex w-full max-w-md items-center justify-between gap-3 rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2 text-sm text-zinc-100 shadow-lg">
            <span className="min-w-0 truncate">{rosterCall.label} aufgerufen</span>
            <button
              type="button"
              onClick={() => void uncall()}
              disabled={uncalling}
              className="min-h-12 shrink-0 rounded px-3 font-bold text-amber-300 underline hover:text-amber-200 disabled:opacity-50"
            >
              Rückgängig
            </button>
          </div>
        </div>
      )}
      {dqSheetOpen && (
        <DqReasonSheet
          shooterLabel={shooterLabel}
          onCancel={() => setDqSheetOpen(false)}
          onConfirm={disqualify}
        />
      )}
      {showComparison && (
        <SnapshotComparison
          beforeSnapshotId={snapshotPair.before?.id ?? null}
          afterSnapshotId={snapshotPair.after?.id ?? null}
          positions={shotPositionRows}
          drift={driftFlagged}
          onClose={() => setShowComparison(false)}
        />
      )}
    </div>
  );
}

interface ScoreTimerBarProps {
  sessionId: string;
  assignmentId: string;
  shooterLabel: string;
  shotCount: number;
  scoringSchemaKind: 'ring_value' | 'hit_factor';
  completedAt: string | null;
  /** Timer safety auto-STOP in seconds; null disables it. Bounds recording
   *  for soft / no-PAR rounds (2×PAR or the session/club max) by STOPPING the
   *  run — never a DQ. Also pushed to the device as its recording cap. */
  maxRecordingSeconds: number | null;
  /** Effective PAR seconds after the referee's per-round override is
   *  resolved against the drill recommendation. NULL when the stage has
   *  no PAR at all. */
  effectiveParSeconds: number | null;
  /** Effective PAR mode. `hard` pushes PAR as the SG-Timer's hard
   *  cap (audible buzzer doubles as the auto-DQ trigger). `soft`
   *  pushes the `maxRecordingSeconds` bound so the device still has an
   *  upper recording limit but the shooter doesn't get a buzzer at PAR. */
  effectiveParMode: 'soft' | 'hard' | 'hard_stop';
  /** ISO arm timestamp; null means the round hasn't been armed yet.
   *  Drives whether `onAutoArm` fires before `source.start()`. */
  roundArmedAt: string | null;
  /** Called once when the SG-Timer Start is tapped and the round
   *  isn't armed yet. Awaited before `source.start()` so the vision
   *  detector starts spinning up alongside the timer's countdown. */
  onAutoArm: () => Promise<void>;
  onOpenTimerDebug?: (() => void) | undefined;
  onTimingsReady: (patch: { firstShotTimeMs?: number; elapsedTimeMs?: number }) => void;
  /** Fired after an auto-DQ on time-out was saved or queued (offline) so
   *  the parent can refetch the roster and surface the DQ banner. */
  onAutoDQ?: ((res: { queued: boolean; authExpired: boolean }) => void) | undefined;
  /** Gets the in-flight auto-DQ, so Bestätigen can wait for it. */
  onAutoDqPending?: ((p: Promise<void>) => void) | undefined;
}

/**
 * Compact timer-control banner above the keypad. Shows the start beep
 * countdown, current run state, live shot count, and the Start/End
 * buttons. Hidden once the round is locked. The aggregate timings get
 * saved onto the round assignment automatically when the source flips
 * to 'stopped' (auto-stop on shotCount, or after the user hits End).
 */
function ScoreTimerBar({
  sessionId,
  assignmentId,
  shooterLabel,
  shotCount,
  scoringSchemaKind,
  completedAt,
  maxRecordingSeconds,
  effectiveParSeconds,
  effectiveParMode,
  roundArmedAt,
  onAutoArm,
  onOpenTimerDebug,
  onTimingsReady,
  onAutoDQ,
  onAutoDqPending,
}: ScoreTimerBarProps) {
  // Only this shooter's run: right after "📣 Aufrufen" the singleton
  // source still holds the previous shooter's stopped run, which must not
  // be saved onto / sent for this assignment.
  const { source, status, isConnected, bleSupported, claimRun } = useTimerRun(assignmentId);
  // A run armed on the timer itself (its own start button, not our Start)
  // belongs to the shooter whose Score screen is open.
  useClaimArmedRun(assignmentId);
  const [busy, setBusy] = useState<string | null>(null);
  // `<sessId>:<shot count>` of the last saved timing snapshot.
  const [savedFor, setSavedFor] = useState<string | null>(null);

  const isVariable = scoringSchemaKind === 'hit_factor' || shotCount <= 0;
  const expectedShots = isVariable ? null : shotCount;

  // Forward the configured shot cap to the source so it auto-stops at
  // the right shot and silently drops overflow.
  useEffect(() => {
    source.setExpectedShots(expectedShots);
  }, [source, expectedShots]);

  // Push the relevant time cap to the device's PAR config. PAR is the only
  // timing concept here:
  //
  // - Hard PAR (referee picked "hart"): push PAR as the timer's hard cap. The
  //   audible buzzer doubles as the cue and the hard-PAR auto-DQ useEffect
  //   below catches the elapsed time and DQs the round.
  //
  // - Soft PAR / no PAR: push the `maxRecordingSeconds` safety bound (2×PAR or
  //   the session/club max) so the device still stops recording, but the
  //   shooter doesn't get a buzzer at PAR — soft PAR is visual-only on the
  //   scoreboard, and the auto-STOP useEffect below ends the run WITHOUT a DQ.
  //   Pushing 0 (when no bound) clears any cap a previous hard round left.
  //
  // Auto-stop after the last expected shot is handled separately via
  // `setExpectedShots` above. Capture the pre-run value once and restore on
  // unmount / stage change so the TimerDebug default isn't trampled.
  useEffect(() => {
    // Only hart-stopp pins the device to PAR (it stops the run there). soft and
    // hart-offen push the recording bound so the device keeps streaming shots
    // past PAR — hart-offen DQs at PAR but must keep recording timings.
    const targetS = effectiveParMode === 'hard_stop' ? effectiveParSeconds : maxRecordingSeconds;
    const original = source.getTimeLimit();
    source.setTimeLimit(targetS != null ? Math.round(targetS * 10) : 0);
    return () => source.setTimeLimit(original);
  }, [source, maxRecordingSeconds, effectiveParSeconds, effectiveParMode]);

  // Latch when the referee taps "Vorzeitig beenden" so the post-grace
  // auto-DQ check can distinguish a manual abort from a hardware
  // time-out. Reset on each new run (keyed by startedAt below).
  const manualEndRef = useRef(false);
  useEffect(() => {
    manualEndRef.current = false;
  }, [status.startedAt]);

  // Latch the run-ending auto-stop (hart-stopp DQ or the soft/hart-offen safety
  // auto-stop) so those two never both fire. Reset on each run.
  const autoEndedRef = useRef(false);
  useEffect(() => {
    autoEndedRef.current = false;
  }, [status.startedAt]);

  // Separate one-shot latch for the hart-offen auto-DQ. That DQ does NOT end
  // the run (recording continues to the shot count / safety bound), so it can't
  // share `autoEndedRef` — but it must still fire only once per run.
  const dqFiredRef = useRef(false);
  useEffect(() => {
    dqFiredRef.current = false;
  }, [status.startedAt]);

  const firstShot = status.shots[0];
  const lastShot = status.shots[status.shots.length - 1];

  // When a run completes, persist the aggregate firstShotTimeMs /
  // elapsedTimeMs onto the round assignment. Idempotent per run + shot
  // count: a shot that lands while a manual stop is in flight saves again.
  useEffect(() => {
    if (status.state !== 'stopped') return;
    if (!firstShot || !lastShot) return;
    const key = `${status.lastSessId ?? -1}:${status.shots.length}`;
    if (savedFor === key) return;
    setSavedFor(key);
    onTimingsReady({
      firstShotTimeMs: firstShot.tFromStartMs,
      elapsedTimeMs: lastShot.tFromStartMs,
    });
  }, [
    status.state,
    status.lastSessId,
    status.shots.length,
    firstShot,
    lastShot,
    savedFor,
    onTimingsReady,
  ]);

  // Push a transient tick to the spectator board on every state /
  // shot change. Best-effort — connectivity loss just means the board
  // misses an update; the durable round timing is independent.
  useEffect(() => {
    if (!isConnected) return;
    if (status.state === 'idle') return;
    void api
      .timerTick({
        rangeSessionId: sessionId,
        roundAssignmentId: assignmentId,
        shooterLabel,
        state: status.state,
        startedAt: status.startedAt,
        firstShotTimeMs: firstShot?.tFromStartMs ?? null,
        lastShotTimeMs: lastShot?.tFromStartMs ?? null,
        shotCount: status.shots.length,
        expectedShots,
        sentAt: Date.now(),
        shotTimesMs: status.shots.map((s) => s.tFromStartMs),
      })
      .catch(() => {
        // Best-effort fan-out — swallow.
      });
  }, [
    isConnected,
    sessionId,
    assignmentId,
    shooterLabel,
    status.state,
    status.startedAt,
    status.shots,
    firstShot?.tFromStartMs,
    lastShot?.tFromStartMs,
    expectedShots,
  ]);

  // Latch a connection drop during an active run (UX review REF-2) so the
  // "Zeiten prüfen" note survives the reconnect until the next run starts.
  // `startedAt` is null when the drop happened while still armed.
  const [drop, setDrop] = useState<{ startedAt: number | null } | null>(null);
  const runActive = status.state === 'armed' || status.state === 'running';
  useEffect(() => {
    if (drop || !status.disconnected || !runActive) return;
    setDrop({ startedAt: status.startedAt ?? null });
  }, [drop, status.disconnected, runActive, status.startedAt]);
  useEffect(() => {
    if (!drop || status.startedAt == null) return;
    if (drop.startedAt == null) setDrop({ startedAt: status.startedAt });
    else if (drop.startedAt !== status.startedAt) setDrop(null);
  }, [drop, status.startedAt]);

  // Hooks below must stay above the early returns: the hook count would
  // otherwise change when `isConnected` flips mid-run (React throws).
  // Safety auto-STOP on the max-recording bound. Armed for soft / no-PAR AND
  // hart-offen runs (hart-offen DQs at PAR but keeps recording, so this bound
  // ends the run at 2×PAR if the shot count is never reached). NOT a
  // disqualification — it just ends a run that would otherwise record forever
  // (variable-shot rounds have no shot cap). Only hart-stopp bails here, since
  // it ends via its own immediate stop+DQ at PAR (below).
  const shotsLenRef = useRef(status.shots.length);
  shotsLenRef.current = status.shots.length;
  useEffect(() => {
    if (maxRecordingSeconds == null) return;
    if (effectiveParMode === 'hard_stop') return;
    if (!isConnected) return;
    if (status.disconnected) return;
    if (status.startedAt == null) return;
    if (status.state !== 'running') return;
    if (completedAt) return;

    const limitMs = maxRecordingSeconds * 1000;
    const remainingMs = status.startedAt + limitMs - Date.now();
    // 250 ms grace lets a shot fired right at the bound land before we
    // stop the run.
    const handle = window.setTimeout(
      () => {
        if (manualEndRef.current) return;
        if (autoEndedRef.current) return;
        if (expectedShots != null && shotsLenRef.current >= expectedShots) return;
        autoEndedRef.current = true;
        void (async () => {
          try {
            await source.stop();
          } catch {
            // Device may already be stopped — non-fatal.
          }
        })();
      },
      Math.max(0, remainingMs) + 250,
    );
    return () => window.clearTimeout(handle);
  }, [
    maxRecordingSeconds,
    effectiveParMode,
    isConnected,
    status.disconnected,
    status.startedAt,
    status.state,
    completedAt,
    expectedShots,
    source,
  ]);

  // Auto-DQ on hard PAR exceed — the only auto-disqualification trigger, in two
  // flavours decided by the round's PAR mode:
  //   * hart-stopp: stop the timer immediately AND DQ (terminal). Latches
  //     `autoEndedRef` (it ends the run, so the safety auto-stop must not also
  //     fire).
  //   * hart-offen: DQ but DO NOT stop — recording continues to the shot count
  //     (device shot-limit) or the 2×PAR safety auto-stop, so a later DQ-revert
  //     keeps the true timings. Uses `dqFiredRef` (one-shot) and deliberately
  //     does NOT latch `autoEndedRef`.
  useEffect(() => {
    if (effectiveParMode !== 'hard' && effectiveParMode !== 'hard_stop') return;
    if (effectiveParSeconds == null) return;
    if (!isConnected) return;
    if (status.disconnected) return;
    if (status.startedAt == null) return;
    if (status.state !== 'running') return;
    if (completedAt) return;

    const terminal = effectiveParMode === 'hard_stop';
    const parMs = effectiveParSeconds * 1000;
    const remainingMs = status.startedAt + parMs - Date.now();
    // Same 250 ms grace as the safety stop so a shot fired right at
    // the buzzer lands before we kill / flag the run.
    const handle = window.setTimeout(
      () => {
        if (manualEndRef.current) return;
        if (expectedShots != null && shotsLenRef.current >= expectedShots) return;
        if (terminal) {
          if (autoEndedRef.current) return;
          autoEndedRef.current = true;
        } else {
          if (dqFiredRef.current) return;
          dqFiredRef.current = true;
        }
        const saving = (async () => {
          if (terminal) {
            try {
              await source.stop();
            } catch {
              // Device may already be stopped — non-fatal.
            }
          }
          try {
            // Offline / behind unsent changes: queued (`round.dq`), FIFO
            // before a later confirm.
            const res = await disqualifyRoundOrQueue(assignmentId, {
              reason: PAR_AUTO_DQ_REASON,
              terminal,
            });
            onAutoDQ?.(res);
          } catch (err) {
            console.error('auto-DQ on hard PAR failed', err);
            alert(
              `PAR überschritten, aber die Auto-Disqualifikation konnte nicht gespeichert werden (${errorText(err)}). Bitte manuell disqualifizieren.`,
            );
          }
        })();
        onAutoDqPending?.(saving);
      },
      Math.max(0, remainingMs) + 250,
    );
    return () => window.clearTimeout(handle);
  }, [
    effectiveParMode,
    effectiveParSeconds,
    isConnected,
    status.disconnected,
    status.startedAt,
    status.state,
    completedAt,
    expectedShots,
    source,
    assignmentId,
    onAutoDQ,
    onAutoDqPending,
  ]);

  if (completedAt) return null;
  if (!bleSupported) return null;

  if (!isConnected && drop) {
    return (
      <button
        type="button"
        onClick={() => onOpenTimerDebug?.()}
        role="alert"
        className="mb-3 w-full rounded-lg border-2 border-red-600 bg-red-950/60 px-3 py-3 text-left text-sm text-red-100"
      >
        <span className="flex items-center gap-2 font-bold">
          <span
            className={`inline-block h-2.5 w-2.5 rounded-full bg-red-500 ${status.reconnecting ? 'animate-pulse' : ''}`}
          />
          Verbindung zum Timer verloren —{' '}
          {status.reconnecting ? 'verbinde neu …' : 'bitte neu koppeln'}
        </span>
        <span className="mt-1 block text-xs text-red-200">
          Bisher {status.shots.length} Schuss vom Timer erfasst. Zeiten prüfen! PAR-Auto-DQ und
          Auto-Stopp sind ausgesetzt — bei Bedarf manuell disqualifizieren.
        </span>
      </button>
    );
  }

  if (!isConnected && status.reconnecting) {
    return (
      <button
        type="button"
        onClick={() => onOpenTimerDebug?.()}
        className="mb-3 flex min-h-12 w-full items-center justify-between rounded-lg border border-amber-800 bg-amber-950/30 px-3 py-2 text-left text-xs text-amber-200 hover:border-amber-600"
      >
        <span className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-amber-400" />
          SG Timer verbindet neu …
        </span>
        <span className="text-emerald-400">Timer →</span>
      </button>
    );
  }

  if (!isConnected) {
    return (
      <button
        type="button"
        onClick={() => onOpenTimerDebug?.()}
        className="mb-3 flex min-h-12 w-full items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-left text-xs text-zinc-300 hover:border-zinc-600"
      >
        <span className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full bg-red-500" />
          SG Timer nicht gekoppelt — Zeitnahme manuell
        </span>
        <span className="text-emerald-400">Koppeln →</span>
      </button>
    );
  }

  async function runWithBusy(label: string, fn: () => Promise<void>) {
    setBusy(label);
    try {
      await fn();
    } catch (err) {
      console.error(`${label} failed`, err);
      alert(
        `${label === 'stop' ? 'Timer-Stopp' : label === 'start' ? 'Timer-Start' : 'Timer'} fehlgeschlagen: ${userErrorText(err, 'ble')}`,
      );
    } finally {
      setBusy(null);
    }
  }

  const onStart = () =>
    runWithBusy('start', async () => {
      setSavedFor(null);
      setDrop(null);
      // Auto-arm the round so vision detection spins up alongside the
      // timer's countdown. Awaited so the supervisor sees the new
      // (location, target) pair before SESSION_START hits the wire.
      // Gated on `isConnected` so a Start tap with a disconnected SG
      // timer (which would error out anyway) doesn't silently arm.
      // Idempotent server-side — re-arms are no-ops.
      if (!roundArmedAt && isConnected) await onAutoArm();
      await source.start();
      // Claimed only after start() reset the shots — claiming earlier
      // would briefly expose the previous run's data as ours.
      claimRun(assignmentId);
    });
  const onEnd = () =>
    runWithBusy('stop', async () => {
      // Manual end (variable-shot or referee abort): write timings now
      // so the round records what we have, then send SESSION_STOP.
      // Latch the manual-end flag first so the auto-DQ wall-clock,
      // armed when this run started, knows the stop wasn't a time-out.
      manualEndRef.current = true;
      // Saved here — the stopped-state effect only saves again if another
      // shot arrives before the stop completes.
      if (lastShot) setSavedFor(`${status.lastSessId ?? -1}:${status.shots.length}`);
      if (firstShot && lastShot) {
        onTimingsReady({
          firstShotTimeMs: firstShot.tFromStartMs,
          elapsedTimeMs: lastShot.tFromStartMs,
        });
      } else if (lastShot) {
        onTimingsReady({ elapsedTimeMs: lastShot.tFromStartMs });
      }
      await source.stop();
    });

  const fmtSec = (ms: number) => formatMs(ms);
  const dot = status.reconnecting
    ? 'bg-amber-400 animate-pulse'
    : status.disconnected
      ? 'bg-red-500'
      : status.state === 'running'
        ? 'bg-emerald-500 animate-pulse'
        : status.state === 'armed'
          ? 'bg-amber-400 animate-pulse'
          : 'bg-zinc-500';

  return (
    <section className="mb-3 rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
      <div className="mb-2 flex items-center justify-between text-xs text-zinc-400">
        <span role="status" className="flex items-center gap-2">
          <span className={`inline-block h-2 w-2 rounded-full ${dot}`} />
          {TIMER_STATE_LABEL[status.state]}
        </span>
        <span className="tabular-nums">
          {expectedShots != null
            ? `${status.shots.length} / ${expectedShots}`
            : `${status.shots.length} Schuss`}
          {firstShot ? ` · 1. ${fmtSec(firstShot.tFromStartMs)}` : ''}
          {lastShot && lastShot !== firstShot ? ` · zuletzt ${fmtSec(lastShot.tFromStartMs)}` : ''}
        </span>
      </div>

      {drop && (
        <p
          role="alert"
          className="mb-2 rounded border border-amber-700 bg-amber-950/40 px-2 py-1.5 text-xs text-amber-200"
        >
          ⚠ Die Verbindung zum Timer war während dieser Runde unterbrochen. Zeiten prüfen und bei
          Bedarf korrigieren; PAR-Auto-DQ und Auto-Stopp waren in der Zeit ausgesetzt.
        </p>
      )}

      {status.state === 'idle' || status.state === 'stopped' ? (
        <button
          onClick={() => void onStart()}
          disabled={busy != null}
          className="w-full rounded-lg bg-emerald-700 px-4 py-3 text-base font-bold text-white hover:bg-emerald-600 disabled:opacity-40"
        >
          ▶ Timer Start
        </button>
      ) : status.state === 'armed' ? (
        <button
          onClick={() => void onEnd()}
          disabled={busy != null}
          className="w-full rounded-lg bg-amber-700 px-4 py-3 text-base font-bold text-white"
        >
          Abbrechen
        </button>
      ) : isVariable ? (
        <button
          onClick={() => void onEnd()}
          disabled={busy != null}
          className="w-full rounded-lg bg-red-700 px-4 py-4 text-lg font-bold text-white hover:bg-red-600 disabled:opacity-40"
        >
          ⏹ Timer Stop
        </button>
      ) : (
        <button
          onClick={() => void onEnd()}
          disabled={busy != null}
          className="min-h-12 w-full rounded-lg border border-red-800 bg-red-950/30 px-4 py-2 text-sm font-bold text-red-300 hover:border-red-500 disabled:opacity-40"
        >
          ⏹ Vorzeitig beenden
        </button>
      )}
    </section>
  );
}
